import { useCallback } from 'react'

import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'

/**
 * Legacy plain-text messages (written before the rich-text editor existed)
 * carry meaning in their newlines. Convert them to paragraphs so the editor
 * shows them faithfully; content that already looks like HTML passes through.
 */
export function plainTextToHtml(text: string): string {
  if (!text) return ''
  if (text.trimStart().startsWith('<')) return text
  return text
    .split(/\n{2,}/)
    .map((para) => `<p>${para.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}</p>`)
    .join('')
}

/** Template variables available in respondent emails. */
export const EMAIL_VARIABLES: { token: string; label: string }[] = [
  { token: '{{first_name}}', label: 'First name' },
  { token: '{{last_name}}', label: 'Last name' },
  { token: '{{programme_name}}', label: 'Programme name' },
  { token: '{{consultancy_name}}', label: 'Consultancy name' },
]

/**
 * Dropdown for inserting a template variable. Used in the editor toolbar and
 * next to subject inputs.
 */
export function VariableMenu({ onInsert }: { onInsert: (token: string) => void }) {
  return (
    <div className="dropdown dropdown-end">
      <div tabIndex={0} role="button" className="btn btn-ghost btn-xs whitespace-nowrap" title="Insert a variable">
        <span className="iconify lucide--braces size-3.5" />
        Insert Variable
        <span className="iconify lucide--chevron-down size-3" />
      </div>
      <ul tabIndex={0} className="dropdown-content menu menu-sm bg-base-100 rounded-box z-20 w-64 p-1.5 shadow-lg border border-base-200">
        {EMAIL_VARIABLES.map((v) => (
          <li key={v.token}>
            <button
              type="button"
              className="flex flex-col items-start gap-0.5 py-1.5"
              onClick={(e) => {
                onInsert(v.token)
                ;(e.currentTarget.closest('.dropdown')?.querySelector('[tabindex]') as HTMLElement)?.blur()
              }}
            >
              <span className="text-sm leading-tight">{v.label}</span>
              <span className="font-mono text-xs text-base-content/50 leading-tight">{v.token}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

interface ToolbarButtonProps {
  command: () => void
  icon: string
  isActive: boolean
  title: string
}

function ToolbarButton({ command, icon, isActive, title }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={title}
      className={`btn btn-ghost btn-xs btn-square ${isActive ? 'btn-active' : ''}`}
      onMouseDown={(e) => {
        e.preventDefault()
        command()
      }}
    >
      <span className={`iconify ${icon} size-4`}></span>
    </button>
  )
}

function FormattingToolbar({ editor, showVariables }: { editor: Editor | null; showVariables: boolean }) {
  const setLink = useCallback(() => {
    if (!editor) return
    const previousUrl = (editor.getAttributes('link').href as string) || ''
    const url = window.prompt('Enter URL', previousUrl || 'https://')
    if (url === null) return
    if (url === '') {
      editor.chain().focus().unsetLink().run()
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run()
    }
  }, [editor])

  if (!editor) return null

  return (
    <div className="flex items-center gap-0.5 flex-wrap px-2 py-1 bg-base-200/30 border-b border-base-300">
      <ToolbarButton title="Bold" icon="lucide--bold" isActive={editor.isActive('bold')} command={() => editor.chain().focus().toggleBold().run()} />
      <ToolbarButton title="Italic" icon="lucide--italic" isActive={editor.isActive('italic')} command={() => editor.chain().focus().toggleItalic().run()} />
      <ToolbarButton title="Underline" icon="lucide--underline" isActive={editor.isActive('underline')} command={() => editor.chain().focus().toggleUnderline().run()} />
      <ToolbarButton title="Strikethrough" icon="lucide--strikethrough" isActive={editor.isActive('strike')} command={() => editor.chain().focus().toggleStrike().run()} />

      <div className="w-px h-5 bg-base-300 mx-1"></div>

      <ToolbarButton title="Heading" icon="lucide--heading-2" isActive={editor.isActive('heading', { level: 2 })} command={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
      <ToolbarButton title="Bullet List" icon="lucide--list" isActive={editor.isActive('bulletList')} command={() => editor.chain().focus().toggleBulletList().run()} />
      <ToolbarButton title="Numbered List" icon="lucide--list-ordered" isActive={editor.isActive('orderedList')} command={() => editor.chain().focus().toggleOrderedList().run()} />

      <div className="w-px h-5 bg-base-300 mx-1"></div>

      <ToolbarButton title="Link" icon="lucide--link" isActive={editor.isActive('link')} command={setLink} />
      {editor.isActive('link') && (
        <ToolbarButton title="Remove Link" icon="lucide--unlink" isActive={false} command={() => editor.chain().focus().unsetLink().run()} />
      )}

      <div className="w-px h-5 bg-base-300 mx-1"></div>

      <ToolbarButton title="Undo" icon="lucide--undo" isActive={false} command={() => editor.chain().focus().undo().run()} />
      <ToolbarButton title="Redo" icon="lucide--redo" isActive={false} command={() => editor.chain().focus().redo().run()} />

      {showVariables && (
        <div className="ms-auto">
          <VariableMenu onInsert={(token) => editor.chain().focus().insertContent(token).run()} />
        </div>
      )}
    </div>
  )
}

interface RichTextEditorProps {
  content: string
  onChange: (html: string) => void
  placeholder?: string
  minHeight?: string
  /** Show the email-variable insert menu in the toolbar. */
  withVariables?: boolean
}

/**
 * Rich text editor for creator-authored survey messages. The server
 * sanitizes on write (RichTextSanitizer) — the toolbar offers only marks
 * inside that allowlist.
 */
export default function RichTextEditor({ content, onChange, placeholder = 'Write your message…', minHeight = 'min-h-32', withVariables = false }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      // StarterKit v3 bundles underline and link; configure them here rather
      // than adding the standalone extensions (which would duplicate).
      StarterKit.configure({
        heading: { levels: [2, 3] },
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
        link: { openOnClick: false, autolink: false },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: plainTextToHtml(content),
    onUpdate: ({ editor: e }) => {
      onChange(e.isEmpty ? '' : e.getHTML())
    },
  })

  return (
    <div className="border border-base-300 rounded-lg overflow-hidden bg-base-100">
      <FormattingToolbar editor={editor} showVariables={withVariables} />
      <EditorContent
        editor={editor}
        className={`survey-rich-text px-4 py-3 text-sm ${minHeight} focus-within:outline-none [&_.tiptap]:outline-none [&_.tiptap]:min-h-48`}
      />
    </div>
  )
}
